"use client";

import React from "react";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from "@/components/ui/tooltip";

type TooltipProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  placement?: "top" | "right" | "bottom" | "left";
  trigger?: "hover" | "click";
};

export default function Tooltip({
  content,
  children,
  className,
  placement = "top",
  trigger = "hover",
}: Readonly<TooltipProps>) {
  const [open, setOpen] = React.useState(false);
  const clickOnly = trigger === "click";

  const triggerChild =
    React.Children.count(children) === 1 && React.isValidElement(children) ? (
      children
    ) : (
      <span>{children}</span>
    );

  return (
    <TooltipProvider delayDuration={200}>
      <UITooltip
        open={open}
        onOpenChange={(nextOpen) => {
          // In click-only mode, ignore hover/focus open requests while still
          // allowing the tooltip to close via outside interactions.
          if (clickOnly && nextOpen) {
            return;
          }
          setOpen(nextOpen);
        }}
      >
        <TooltipTrigger
          asChild
          onClick={(event) => {
            if (clickOnly) {
              event.preventDefault();
            }

            setOpen((prevOpen) => !prevOpen);
          }}
        >
          {triggerChild}
        </TooltipTrigger>
        <TooltipContent side={placement} className={className}>
          {content}
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}
